import type { FileExtractionResult, MergedExtractionData } from "./types";
import type { EntityData } from "@/types/entity";
import type { ContactData } from "@/types/contact";
import type { ImpactAnalysisData } from "@/types/impact-analysis";

export function createEmptyMergedData(): MergedExtractionData {
  return {
    entityData: {} as Partial<EntityData>,
    contactData: {} as Partial<ContactData>,
    impactAnalysisData: {} as Partial<ImpactAnalysisData>,
    notificationDecision: null,
    riskAnalysis: null,
    romanianStateRelationship: null,
    euServiceProvision: null,
    article9: null,
    evaluarePreliminaraData: null,
  };
}

export function mergeFileResults(
  fileResults: FileExtractionResult[]
): MergedExtractionData {
  const merged = createEmptyMergedData();

  for (const result of fileResults) {
    if (!result.isValid || !result.transformedData) continue;

    const { documentType, data } = result.transformedData;

    switch (documentType) {
      case "notification":
        merged.entityData = deepMergePartial(
          merged.entityData,
          data.entityData
        );
        merged.contactData = deepMergePartial(
          merged.contactData,
          data.contactData
        );
        merged.romanianStateRelationship = data.romanianStateRelationship;
        merged.euServiceProvision = data.euServiceProvision;
        merged.article9 = data.article9;
        merged.evaluarePreliminaraData = data.evaluarePreliminaraData;
        break;
      case "impact":
        merged.impactAnalysisData = deepMergePartial(
          merged.impactAnalysisData,
          data.impactAnalysisData
        );
        break;
      case "notification_decision":
        merged.notificationDecision = data;
        break;
      case "risk_analysis":
        merged.riskAnalysis = data;
        break;
    }
  }

  return merged;
}

function deepMergePartial<T extends Record<string, unknown>>(
  target: Partial<T>,
  source: Partial<T>
): Partial<T> {
  const result = { ...target } as Record<string, unknown>;

  for (const key of Object.keys(source)) {
    const sourceVal = source[key as keyof T];

    if (sourceVal === undefined || sourceVal === null) continue;

    if (Array.isArray(sourceVal)) {
      result[key] = sourceVal;
    } else if (typeof sourceVal === "object" && !Array.isArray(sourceVal)) {
      const targetVal = result[key];
      if (
        targetVal &&
        typeof targetVal === "object" &&
        !Array.isArray(targetVal)
      ) {
        result[key] = deepMergePartial(
          targetVal as Record<string, unknown>,
          sourceVal as Record<string, unknown>
        );
      } else {
        result[key] = sourceVal;
      }
    } else {
      result[key] = sourceVal;
    }
  }

  return result as Partial<T>;
}
